import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

/**
 * Composable: useBuscarReferidos
 *
 * Owns the referido promotion state for the public abonos resumen
 * (BuscarReferidosComponent).
 *
 * Single captcha per component: the buscar -> confirmar sequence is one short
 * promotional flow, both calls happen seconds apart from the same actor, so
 * the same Turnstile token (single-use, ~300s lifetime) travels in both
 * payloads. One challenge per flow, reset on any failure.
 */
export function useBuscarReferidos() {
    const n_abonado = ref(null);
    const abonado = ref(null);
    const promocion_confirmada = ref(false);
    const loading = ref(false);
    const captchaToken = ref(null);
    const sitekey = process.env.VUE_APP_CLOUDFLARE_SITE_KEY;

    const configuracion = computed(() => store.getters.getConfig);

    const canSubmit = computed(() => !!captchaToken.value && !loading.value);

    const onCaptchaVerified = token => {
        captchaToken.value = token;
    };

    /**
     * Turnstile tokens expire while the user is still on the page, and the
     * widget also reports hard errors. Both must invalidate the stored token,
     * or canSubmit would stay true and the form would submit a stale token.
     */
    const onCaptchaInvalid = () => {
        captchaToken.value = null;
    };

    /**
     * Turnstile tokens are single-use: once the backend has consumed one it
     * can never be reused. Every failed attempt must hand the user a fresh
     * token or they get stuck unable to retry.
     */
    const resetCaptcha = captchaRef => {
        captchaToken.value = null;
        if (captchaRef && typeof captchaRef.reset === 'function') {
            captchaRef.reset();
        }
    };

    const buscar = async() => {
        if (!canSubmit.value) return { ok: false };

        loading.value = true;

        try {
            const res = await store.dispatch(config.getVuexAlias('front.abonados.buscar.referido'), {
                filters: {
                    tid: configuracion.value && configuracion.value.hash_teatro
                },
                n_abonado: n_abonado.value,
                captchaToken: captchaToken.value
            });
            abonado.value = res.data.data;
            return { ok: true, data: res.data.data };
        } catch (error) {
            return { ok: false, error };
        } finally {
            loading.value = false;
        }
    };

    const confirmarAbonado = async(session_id, captchaRef) => {
        if (!canSubmit.value || !abonado.value) return { ok: false };

        loading.value = true;

        try {
            await store.dispatch(config.getVuexAlias('front.abonados.confirmar.referido'), {
                filters: {
                    tid: configuracion.value && configuracion.value.hash_teatro
                },
                abonado_id: abonado.value.id,
                session_id: session_id,
                captchaToken: captchaToken.value
            });
            promocion_confirmada.value = true;
            return { ok: true };
        } catch (error) {
            resetCaptcha(captchaRef);
            return { ok: false, error };
        } finally {
            loading.value = false;
        }
    };

    const cancelar = () => {
        abonado.value = null;
        n_abonado.value = null;
    };

    return {
        n_abonado,
        abonado,
        promocion_confirmada,
        loading,
        captchaToken,
        sitekey,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        resetCaptcha,
        buscar,
        confirmarAbonado,
        cancelar
    };
}
